import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { getMatchStrength } from './regressionCoveragePresentation';
import { DECISION_CHIP_MAP } from './regressionPartialReviewPresentation';

const DECISIONS = [
  { value: 'accepted_as_sufficient', label: 'Accept as sufficient' },
  { value: 'needs_targeted_test', label: 'Needs targeted test' },
  { value: 'needs_product_knowledge_refinement', label: 'Needs product knowledge refinement' },
  { value: 'not_applicable', label: 'Not applicable' },
  { value: 'deferred', label: 'Defer' },
];

const DECISION_CHIP = DECISION_CHIP_MAP;

const bestMatchedTestLabel = (unit = {}) => {
  const tests = unit.matched_tests || [];
  if (!tests.length) return '—';
  const best = tests[0];
  return best.test_title || best.test_id || '—';
};

const decisionChipFor = (decision) => {
  const info = DECISION_CHIP[decision];
  if (!info) return null;
  return (
    <Chip
      size="small"
      label={info.label}
      color={info.color}
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  );
};

const PartialUnitCard = ({
  unit,
  decision,
  comment,
  saving,
  onDecisionChange,
  onCommentChange,
  onSave,
}) => {
  const strength = getMatchStrength(unit.match_score);
  const review = unit.partial_review || null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {unit.unit_name || unit.unit_text || '-'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {unit.module_key || '-'} · {unit.unit_type || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Source: {(unit.source && (unit.source.pkb_entry_title || unit.source.pkb_entry_key)) || '-'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25 }}>
            Best matched test: {bestMatchedTestLabel(unit)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            Match strength: {strength.label}{strength.raw != null ? ` (${strength.raw.toFixed(2)})` : ''}
          </Typography>
          {unit.match_reason && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
              Reason: {unit.match_reason}
            </Typography>
          )}
          {review?.decision && (
            <Box sx={{ mt: 0.75 }}>
              {decisionChipFor(review.decision)}
              {review.reviewer_comment && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  Comment: {review.reviewer_comment}
                </Typography>
              )}
            </Box>
          )}
        </Box>
        <Chip size="small" label="Partial match" color="warning" variant="outlined" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: 1.25 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id={`decision-${unit.coverage_unit_id}`}>Decision</InputLabel>
          <Select
            labelId={`decision-${unit.coverage_unit_id}`}
            value={decision || ''}
            label="Decision"
            onChange={(e) => onDecisionChange(e.target.value)}
          >
            {DECISIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          fullWidth
          label="Comment"
          value={comment || ''}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder={decision === 'not_applicable' ? 'Required for Not applicable' : 'Optional'}
        />
      </Box>
      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size="small"
          onClick={onSave}
          disabled={saving || !decision || (decision === 'not_applicable' && !(comment || '').trim())}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

const RegressionPartialMatchReviewDialog = ({
  open,
  onClose,
  apiClient,
  projectId,
  validationRunId,
  initialUnits = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState(initialUnits);
  const [error, setError] = useState(null);
  const [savingUnitId, setSavingUnitId] = useState(null);
  const [draftByUnitId, setDraftByUnitId] = useState({});

  useEffect(() => {
    if (!open || !validationRunId || !apiClient) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiClient.makeRequest(
          `/regression/runs/${validationRunId}/partial-reviews?project_id=${projectId}`,
          { method: 'GET' }
        );
        if (!response?.success) {
          throw new Error(response?.detail || response?.message || 'Failed to load partial reviews');
        }
        if (!cancelled) {
          setUnits(response.partial_units || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load partial reviews');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, validationRunId, apiClient, projectId]);

  const titleSuffix = useMemo(() => (
    units.length ? `(${units.length})` : ''
  ), [units.length]);

  const handleSave = async (unit) => {
    const unitId = unit.coverage_unit_id;
    const draft = draftByUnitId[unitId] || {};
    const decision = draft.decision;
    const comment = draft.comment;
    if (!decision) return;
    if (decision === 'not_applicable' && !(comment || '').trim()) return;
    try {
      setSavingUnitId(unitId);
      setError(null);
      const response = await apiClient.makeRequest(
        `/regression/runs/${validationRunId}/partial-reviews/${unitId}?project_id=${projectId}`,
        {
          method: 'POST',
          body: JSON.stringify({ decision, reviewer_comment: comment }),
        }
      );
      if (!response?.success) {
        throw new Error(response?.detail || response?.message || 'Failed to save review');
      }
      const saved = response.review || {};
      setUnits((prev) => prev.map((u) => (
        u.coverage_unit_id === unitId
          ? { ...u, partial_review: { ...u.partial_review, ...saved } }
          : u
      )));
    } catch (err) {
      setError(err.message || 'Failed to save review');
    } finally {
      setSavingUnitId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Review Partial Matches {titleSuffix}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Partial matches do not count as covered until reviewed. Record whether the existing test is sufficient, a targeted test is needed, or the product knowledge needs refinement.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {loading && <Alert severity="info" sx={{ mb: 1.5 }}>Loading partial matches…</Alert>}
        {!loading && units.length === 0 && (
          <Alert severity="info">No partial matches to review.</Alert>
        )}
        {!loading && units.map((unit) => {
          const unitId = unit.coverage_unit_id;
          const draft = draftByUnitId[unitId] || {};
          return (
            <PartialUnitCard
              key={unitId}
              unit={unit}
              decision={draft.decision}
              comment={draft.comment}
              saving={savingUnitId === unitId}
              onDecisionChange={(value) => setDraftByUnitId((prev) => ({ ...prev, [unitId]: { ...prev[unitId], decision: value } }))}
              onCommentChange={(value) => setDraftByUnitId((prev) => ({ ...prev, [unitId]: { ...prev[unitId], comment: value } }))}
              onSave={() => handleSave(unit)}
            />
          );
        })}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="text" onClick={onClose}>Close</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RegressionPartialMatchReviewDialog;

